import PocketBase from 'pocketbase';
import axios from "axios";
import qs from "query-string";
import toast, { Toaster } from 'react-hot-toast';
const pb = new PocketBase('https://dbap.a-traq.com');
const API_BASE_URL = 'https://dbap.a-traq.com/api/collections';

const effectiveSeats = (record) =>
  record?.genesis === true ? (Number(record.Seats) || 0) : 0;

const mapCompanyRecord = (record) => ({
  id: record?.id,
  companyName: record.CompanyName,
  email: Array.isArray(record?.expand?.Administrator) && record.expand.Administrator.length > 0 ? record.expand.Administrator[0].email : "-",
  country: record?.expand?.Country?.Country || "-",
  noOfModule: effectiveSeats(record),
  active: record?.active !== false,
});

const escapeFilterValue = (value) => String(value ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const flattenRelationIds = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
};

function generateContactPassword() {
  const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let password = "";
  for (let i = 0; i < 10; i += 1) {
    password += chars[Math.floor(Math.random() * chars.length)];
  }
  return password;
}

const fetchCompanies = async () => {
    pb.autoCancellation(false);
    const records = await pb.collection('Customers').getFullList({
      expand : "Administrator, Country",
    });
    return records.map(mapCompanyRecord);
  };

  const fetchCompanyById = async (companyId) => {
    pb.autoCancellation(false);
    const record = await pb.collection("Customers").getOne(companyId, {
      expand: "Administrator, Country",
    });
    return mapCompanyRecord(record);
  };

  const searchCompanies = async (query) => {
    try {
      const trimmedQuery = (query ?? "").trim();
      if (!trimmedQuery) {
        return await fetchCompanies();
      }

      const safeQuery = escapeFilterValue(trimmedQuery);

      // Step 1: Search in Contacts collection
      const contactsResponse = await pb.collection("Contacts").getFullList({
        filter : `email~"${safeQuery}"||Name~"${safeQuery}"||Surname~"${safeQuery}"||MobileNumber~"${safeQuery}"`,
      })
      const customerIds = [...new Set(contactsResponse.flatMap((c) => flattenRelationIds(c.CompanyID)))];
  
      // Step 2: Search Customers directly using companyName
      const customersByNameResponse = await pb.collection("Customers").getFullList({
        filter : `CompanyName~"${safeQuery}"`,
        expand : "Administrator, Country",
     });
  
      // Step 3: Search Customers using IDs from Contacts
      let customersById = [];
      if (customerIds.length > 0) {
        const customersByIdResponse = await pb.collection("Customers").getFullList({
          filter : customerIds.map((id) => `id="${escapeFilterValue(id)}"`).join("||"),
          expand : "Administrator, Country",
      });
        
        customersById = customersByIdResponse;
      }
  
      // Merge results while avoiding duplicates
      const allCustomers = [...customersByNameResponse, ...customersById];
      const uniqueCustomers = [...new Map(allCustomers.map((c) => [c.id, c])).values()];
      
      return uniqueCustomers.map(mapCompanyRecord);
    } catch (error) {
      console.error("Error fetching companies:", error);
      return [];
    }
  };

  const updateCompanyActiveStatus = async (companyId, active) => {
    try {
      const record = await pb.collection("Customers").update(companyId, { active });
      return record;
    } catch (error) {
      console.error("Error updating company status:", error);
      toast.error("Failed to update company status.");
      throw error;
    }
  };

  const createCustomer = async (data) => {
    try {
      // fetching the customer data ... 
   
      const customerData = {
        CompanyName : data.CompanyName,
        CommercialName : data.CommercialName,
        InvoiceAddress : data.InvoiceAddress,
        Telephone1: data.Telephone1,
        Country : data.Country,
      } 
      const record = await pb.collection('Customers').create(customerData);
      const generatedPassword = generateContactPassword();
      const adminData = {
        Name : data.SuperAdminName,
        Surname : data.SuperAdminSurname,
        email : data.SuperAdminEmail,
        MobileNumber : data.SuperAdminMobile,
        CompanyID : record.id,
        emailVisibility : true,
        password : generatedPassword,
        passwordConfirm : generatedPassword,
        CompanyId : record.id,
        CanAccessPortal: true,
        AccessRight : "Administrator"
      }
      const adminRecord = await pb.collection('Contacts').create(adminData);
      toast.success('Customer created successfully!');

    } catch (error) {
      toast.error('Failed to create customer. Please try again.');
      throw error;
    }
  }

  const fetchCountries = async () => {
  try {
    const records = await pb.collection("Countries").getFullList({ sort: "Country" });
    return records.map((record) => ({ id: record.id, name: record.Country }));
  } catch (error) {
    console.error("Error fetching countries:", error);
    return [];
  }
};

  const fetchAllDocuments = async (id) => {
    try {
      const documents = await pb.collection("Documents").getFullList({
        filter : `CompanyId="${id}"`
      });
      return documents;
    } catch (error) {
      
      return [];
    }
  }

  const checkEmailExists = async (email) => {
    try {
      const contacts = await pb.collection("Contacts").getFullList({
        filter: `email="${email}"`,
        fields: "id,email"
      });
      return contacts.length > 0;
    } catch (error) {
      console.error("Error checking email existence:", error);
      return false;
    }
  };

  const fetchAllContacts = async () => {
    try {
      const contacts = await pb.collection("Contacts").getFullList({
        fields: "id,Name,Surname,email"
      });
      return contacts;
    } catch (error) {
      console.error("Error fetching contacts:", error);
      return [];
    }
  };

  const fetchSupportMembers = async () => {
    try {
      const contacts = await pb.collection("Contacts").getFullList({
        filter: 'IsTicketSupport=true && (isDeleted=false || isDeleted="")',
        fields: "id,Name,Surname,email"
      });
      return contacts;
    } catch (error) {
      console.error("Error fetching support members:", error);
      return [];
    }
  };

  const getCustomersCount = async () => {
    try {
      const result = await pb.collection("Customers").getList(1, 1);
      return result.totalItems;
    } catch (error) {
      console.error("Error fetching customers count:", error);
      return 0;
    }
  };

const fetchTickets = async (filterType = 'open', companyId = null, sortType = 'created') => {
    try {
      pb.autoCancellation(false);
      // Filter for tickets where reference is empty (Main/Parent tickets)
      let filter = `reference="" && (filterType === 'open' ? 'TicketClose = ""' : 'TicketClose != ""')`;
      if (filterType === 'open') {
          filter = `reference="" && TicketClose = ""`;
      } else {
          filter = `reference="" && TicketClose != ""`;
      }
      
      if (companyId && companyId !== "") {
          filter += ` && CompanyID="${companyId}"`;
      }
      
      let sort = '+is_support_reply,-updated'; // default: un-replied tickets first
      if (sortType === 'needs_reply') sort = '+is_support_reply,-updated';
      else if (sortType === 'created_desc') sort = '-created';
      else if (sortType === 'created_asc') sort = '+created';
      else if (sortType === 'priority_desc') sort = '-priority';
      else if (sortType === 'priority_asc') sort = '+priority';
      else if (sortType === 'updated_desc') sort = '-updated';
      else if (sortType === 'updated_asc') sort = '+updated';

      const records = await pb.collection('Tickets').getFullList({
        sort: sort,
        filter: filter,
        expand: 'CompanyID,SupportMember'
      });
      return records;
    } catch (error) {
      console.error("Error fetching tickets:", error);
      return [];
    }
  };

  const getOpenTicketsCount = async () => {
    try {
      const result = await pb.collection('Tickets').getList(1, 1, {
        filter: 'reference="" && TicketClose = ""',
      });
      return result.totalItems;
    } catch (error) {
      console.error("Error fetching open tickets count:", error);
      return 0;
    }
  };

// ... fetchTicketById remains same ...

  const fetchTicketById = async (id) => {
    try {
      pb.autoCancellation(false);
      const record = await pb.collection('Tickets').getOne(id, {
        expand: 'CompanyID,SupportMember'
      });
      return record;
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      throw error;
    }
  };

  const fetchChildTickets = async (parentId) => {
    try {
        pb.autoCancellation(false);
        const records = await pb.collection('Tickets').getFullList({
            sort: '+updated', // Sorted by updated column ascending
            filter: `reference="${parentId}"`,
            expand: 'CompanyID'
        });
        return records;
    } catch (error) {
        console.error("Error fetching child tickets:", error);
        return [];
    }
  };

  const createTicketReply = async (data) => {
    try {
        const record = await pb.collection('Tickets').create(data);
        return record;
    } catch (error) {
        console.error("Error creating ticket reply:", error);
        throw error;
    }
  };

  const updateTicket = async (id, data) => {
    try {
        const record = await pb.collection('Tickets').update(id, data);
        return record;
    } catch (error) {
        console.error("Error updating ticket:", error);
        throw error;
    }
  };

  const fetchTicketPresets = async () => {
    try {
        pb.autoCancellation(false);
        const records = await pb.collection('Ticket_Presets').getFullList({
            sort: 'preset_name',
        });
        return records;
    } catch (error) {
        console.error("Error fetching ticket presets:", error);
        return [];
    }
  };

const fetchDashboardStats = async () => {
  pb.autoCancellation(false);
  const [customers, openTickets] = await Promise.all([
    pb.collection("Customers").getFullList({ fields: "id,created,active,Seats,genesis" }),
    getOpenTicketsCount(),
  ]);

  const totalCustomers = customers.length;
  const inactiveCount = customers.filter((customer) => customer.active === false).length;
  const churnRate = totalCustomers ? Number(((inactiveCount / totalCustomers) * 100).toFixed(1)) : 0;
  const totalSeats = customers.reduce((sum, customer) => sum + effectiveSeats(customer), 0);

  const now = new Date();
  const months = [];
  for (let offset = 5; offset >= 0; offset -= 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - offset + 1, 1);
    months.push({
      month: start.toLocaleString("en", { month: "short" }),
      start,
      end,
    });
  }

  let runningTotal = customers.filter((customer) => new Date(customer.created) < months[0].start).length;
  const growthData = months.map((bucket) => {
    const createdThisMonth = customers.filter((customer) => {
      const created = new Date(customer.created);
      return created >= bucket.start && created < bucket.end;
    });
    const inactiveThisMonth = createdThisMonth.filter((customer) => customer.active === false).length;
    runningTotal += createdThisMonth.length;
    return {
      month: bucket.month,
      customers: runningTotal,
      newCustomers: createdThisMonth.length,
      churnedCustomers: inactiveThisMonth,
    };
  });

  const thisMonth = growthData[growthData.length - 1] || { customers: 0, newCustomers: 0 };
  const prevMonth = growthData[growthData.length - 2];
  const trendValue = prevMonth ? thisMonth.customers - prevMonth.customers : thisMonth.newCustomers;
  const customerTrend = `${trendValue >= 0 ? "+" : ""}${trendValue}`;

  return {
    totalCustomers,
    openTickets,
    totalSeats,
    churnRate,
    growthData,
    customerTrend,
    newThisMonth: thisMonth.newCustomers || 0,
  };
};

const listCoupons = async (limit = 10, page = 1) => {
  const result = await pb.collection("Coupons").getList(page, limit, { sort: "-created" });
  return { success: true, coupons: result.items, totalItems: result.totalItems };
};

const createCoupon = async (payload) => {
  const record = await pb.collection("Coupons").create({
    code: payload.code || "",
    discountType: payload.discountType || "",
    discountValue: String(payload.discountValue ?? ""),
    currency: payload.currency || "",
    maxRedemptions: payload.maxRedemptions != null ? String(payload.maxRedemptions) : "",
    duration: payload.duration || "",
    durationInMonths: payload.durationInMonths != null ? String(payload.durationInMonths) : "",
    validFrom: payload.validFrom || "",
    validUntil: payload.validUntil || "",
    minSubscriptionAmount: payload.minSubscriptionAmount != null ? String(payload.minSubscriptionAmount) : "",
    productIds: Array.isArray(payload.productIds) ? payload.productIds.join(",") : (payload.productIds || ""),
    description: payload.description || "",
    timesUsed: "0",
    isActive: true,
  });
  return { success: true, coupon: record };
};

const validateCoupon = async (code) => {
  const safeCode = escapeFilterValue((code || "").trim());
  const result = await pb.collection("Coupons").getList(1, 1, {
    filter: `code="${safeCode}"`,
  });
  const coupon = result.items[0];
  if (!coupon) {
    return { valid: false };
  }

  const now = Date.now();
  const validFrom = coupon.validFrom ? Number(coupon.validFrom) : null;
  const validUntil = coupon.validUntil ? Number(coupon.validUntil) : null;
  const maxRedemptions = coupon.maxRedemptions ? Number(coupon.maxRedemptions) : null;
  const timesUsed = Number(coupon.timesUsed || 0);
  const isActive = coupon.isActive !== false;
  const inDateRange = (!validFrom || now >= validFrom) && (!validUntil || now <= validUntil);
  const underLimit = !maxRedemptions || timesUsed < maxRedemptions;

  return {
    valid: isActive && inDateRange && underLimit,
    coupon,
  };
};

const deactivateCoupon = async (couponId) => {
  await pb.collection("Coupons").update(couponId, { isActive: false });
  return { success: true };
};

const fetchCustomerBilling = async (companyId) => {
  return pb.collection("Customers").getOne(companyId, {
    expand: "Country,InvoiceCountry,Administrator",
  });
};

const fetchTeamContacts = async () => {
  try {
    pb.autoCancellation(false);
    const contacts = await pb.collection("Contacts").getFullList({
      filter: 'isInternal=true && (isDeleted=false || isDeleted="")',
      fields: "id,Name,Surname,email,isInternal",
      sort: "Name",
    });
    return contacts;
  } catch (error) {
    console.error("Error fetching team contacts:", error);
    return [];
  }
};

const fetchTaskStatuses = async () => {
  pb.autoCancellation(false);
  return pb.collection("Task_Statuses").getFullList({ sort: "SortOrder" });
};

const buildTasksFilter = ({
  includeArchived = false,
  search = "",
  priority = "",
  assignee = "",
  statusId = "",
  category = "",
} = {}) => {
  const parts = [];
  if (!includeArchived) parts.push("IsArchived != true");
  if (priority) parts.push(`Priority="${escapeFilterValue(priority)}"`);
  if (category) parts.push(`Category="${escapeFilterValue(category)}"`);
  if (assignee === "unassigned") parts.push('AssignedTo = "[]"');
  else if (assignee) parts.push(`AssignedTo ~ "${escapeFilterValue(assignee)}"`);
  if (statusId) parts.push(`Status="${escapeFilterValue(statusId)}"`);
  if (search.trim()) {
    const q = escapeFilterValue(search.trim());
    parts.push(`Title~"${q}"`);
  }
  return parts.join(" && ");
};

const fetchTasks = async (options = {}) => {
  pb.autoCancellation(false);
  const filter = buildTasksFilter(options);
  const query = {
    filter: filter || undefined,
    sort: "-created",
    expand: "Status,AssignedTo",
  };

  try {
    return await pb.collection("Tasks").getFullList(query);
  } catch (error) {
    console.error("Error fetching tasks with expand:", error);
    try {
      return await pb.collection("Tasks").getFullList({
        filter: filter || undefined,
        sort: "-created",
      });
    } catch (fallbackError) {
      console.error("Error fetching tasks:", fallbackError);
      throw fallbackError;
    }
  }
};

const fetchTaskById = async (id) => {
  pb.autoCancellation(false);
  return pb.collection("Tasks").getOne(id, {
    expand: "Status,AssignedTo,CreatedBy",
  });
};

const getDefaultTaskStatus = async () => {
  const statuses = await fetchTaskStatuses();
  return statuses.find((s) => s.IsDefault) || statuses[0] || null;
};

const getNextTaskPosition = async (statusId) => {
  const tasks = await pb.collection("Tasks").getFullList({
    filter: `Status="${escapeFilterValue(statusId)}" && (IsArchived=false || IsArchived=null || IsArchived="")`,
    sort: "-Position",
    fields: "Position",
  });
  const maxPosition = tasks.reduce((max, task) => Math.max(max, Number(task.Position) || 0), 0);
  return maxPosition + 1;
};

const createTask = async (data) => {
  const record = await pb.collection("Tasks").create(data);
  return record;
};

const updateTask = async (id, data) => {
  const record = await pb.collection("Tasks").update(id, data);
  return record;
};

const createTaskActivity = async (data) => {
  return pb.collection("Task_Activity").create(data);
};

const logTaskActivity = async ({ taskId, actorId, action, message, metadata = null }) => {
  try {
    return await createTaskActivity({
      Task: taskId,
      Actor: actorId || "",
      Action: action,
      Message: message,
      Metadata: metadata,
    });
  } catch (error) {
    console.error("Error logging task activity:", error);
    return null;
  }
};

const fetchTaskComments = async (taskId) => {
  return pb.collection("Task_Comments").getFullList({
    filter: `Task="${escapeFilterValue(taskId)}"`,
    sort: "-created",
    expand: "Author",
  });
};

const createTaskComment = async (data) => {
  return pb.collection("Task_Comments").create(data);
};

const fetchTaskActivity = async (taskId) => {
  return pb.collection("Task_Activity").getFullList({
    filter: `Task="${escapeFilterValue(taskId)}"`,
    sort: "-created",
    expand: "Actor",
  });
};

export { fetchCompanies , fetchCompanyById, searchCompanies, createCustomer, fetchCountries, fetchAllDocuments, checkEmailExists, getCustomersCount, fetchAllContacts, fetchSupportMembers, fetchTickets, getOpenTicketsCount, fetchTicketById, fetchChildTickets, createTicketReply, updateTicket, fetchTicketPresets, updateCompanyActiveStatus, fetchDashboardStats, listCoupons, createCoupon, validateCoupon, deactivateCoupon, fetchCustomerBilling, generateContactPassword, effectiveSeats, fetchTeamContacts, fetchTaskStatuses, fetchTasks, fetchTaskById, getDefaultTaskStatus, getNextTaskPosition, createTask, updateTask, createTaskActivity, logTaskActivity, fetchTaskComments, createTaskComment, fetchTaskActivity, pb };
